import { useEffect, useState } from "react";

import { formatDate } from "@usas/ui-web/utils/DateFunctions";

const INITIAL_SELECTED_NON_ATHLETES_STATE = {
  nonAthletesToSync: [],
  nonAthletesToTransfer: []
};

const useClubNonAthleteRosterFormGrid = (gridData, onSubmitNonAthleteFilter, onSubmitNonAthleteSync, onSubmitNonAthleteTransfer, onErrorCaught) => {
  const [formGridState, setFormGridState] = useState([]);
  const [selectedNonAthletesState, setSelectedNonAthletesState] = useState(INITIAL_SELECTED_NON_ATHLETES_STATE);
  const [displayTransferPopupState, setDisplayTransferPopupState] = useState(false);

  const onClearFilterClicked = (event, formState) => {
    try {
      event?.preventDefault();

      setFormGridState(createFormGridState(gridData, [], []));
      setSelectedNonAthletesState({ nonAthletesToSync: [], nonAthletesToTransfer: [] });

      onSubmitNonAthleteFilter(formState);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onSyncSelectedClicked = (event) => {
    try {
      event?.preventDefault();

      const newFormGridState = createFormGridState(gridData, [], selectedNonAthletesState.nonAthletesToTransfer);
      setFormGridState(newFormGridState);
      setSelectedNonAthletesState(createSelectedNonAthletesState(newFormGridState, [], selectedNonAthletesState.nonAthletesToTransfer));

      onSubmitNonAthleteSync(selectedNonAthletesState.nonAthletesToSync);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferSelectedClicked = (event) => {
    try {
      event?.preventDefault();

      setDisplayTransferPopupState(true);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferPopupConfirmClicked = (event) => {
    try {
      event?.preventDefault();

      const newFormGridState = createFormGridState(gridData, selectedNonAthletesState.nonAthletesToSync, []);

      setFormGridState(newFormGridState);
      setSelectedNonAthletesState(createSelectedNonAthletesState(newFormGridState, selectedNonAthletesState.nonAthletesToSync, []));

      setDisplayTransferPopupState(false);
      onSubmitNonAthleteTransfer(selectedNonAthletesState.nonAthletesToTransfer);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferPopupCancelClicked = (event) => {
    try {
      event?.preventDefault();

      setDisplayTransferPopupState(false);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onUpdateIsSyncSelected = (memberId) => {
    try {
      const newFormGridState = JSON.parse(JSON.stringify(formGridState));

      for (let i = 0; i < newFormGridState.length; i++) {
        if (newFormGridState[i].memberId === memberId) {
          newFormGridState[i].isSyncSelected = !newFormGridState[i].isSyncSelected;

          break;
        }
      }

      setFormGridState(newFormGridState);
      setSelectedNonAthletesState(createSelectedNonAthletesState(newFormGridState, selectedNonAthletesState.nonAthletesToSync, selectedNonAthletesState.nonAthletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onUpdateIsTransferSelected = (personOrgRoleDurationId) => {
    try {
      const newFormGridState = JSON.parse(JSON.stringify(formGridState));

      for (let i = 0; i < newFormGridState.length; i++) {
        const role = newFormGridState[i].roles.find((r) => r.personOrgRoleDurationId === personOrgRoleDurationId);
        if (role) {
          role.isTransferSelected = !role.isTransferSelected;
          break;
        }
      }

      setFormGridState(newFormGridState);
      setSelectedNonAthletesState(createSelectedNonAthletesState(newFormGridState, selectedNonAthletesState.nonAthletesToSync, selectedNonAthletesState.nonAthletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  useEffect(() => {
    try {
      const newFormGridState = createFormGridState(gridData, selectedNonAthletesState.nonAthletesToSync, selectedNonAthletesState.nonAthletesToTransfer);

      setFormGridState(newFormGridState);
      setSelectedNonAthletesState(createSelectedNonAthletesState(newFormGridState, selectedNonAthletesState.nonAthletesToSync, selectedNonAthletesState.nonAthletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  function createFormGridState(gridData, nonAthletesToSync = [], nonAthletesToTransfer = []) {
    const newFormGridState = [];

    for (const clubNonAthleteObj of gridData) {
      const newClubNonAthleteFormObj = createClubNonAthleteFormObj(clubNonAthleteObj, nonAthletesToSync, nonAthletesToTransfer);

      newFormGridState.push(newClubNonAthleteFormObj);
    }

    return newFormGridState;
  }

  function createClubNonAthleteFormObj(clubNonAthleteObj, nonAthletesToSync = [], nonAthletesToTransfer = []) {
    const roles = clubNonAthleteObj.goodStandingOrgRole.map((role) => ({
      orgRoleId: role.orgRoleId,
      personOrgRoleDurationId: role.personOrgRoleDurationId,
      orgRoleName: role.orgRoleName,
      registrationExpirationDate: role.registrationExpirationDate ? formatDate(role.registrationExpirationDate) : '',
      isInGoodStandingExpirationDate: role.isInGoodStandingExpirationDate ? formatDate(role.isInGoodStandingExpirationDate) : '',
      unClubId: role.unClubId,
      isTransferPending: role.isTransferPending === true,
      isTransferSelected: role.isTransferPending !== true
        ? nonAthletesToTransfer.some((x) => x.personOrgRoleDurationId === role.personOrgRoleDurationId) === true
        : false
    }));

    return {
      personId: clubNonAthleteObj.personId,
      memberId: clubNonAthleteObj.memberId,
      firstName: clubNonAthleteObj.firstName,
      preferredName: clubNonAthleteObj.preferredName,
      lastName: clubNonAthleteObj.lastName,
      birthDate: clubNonAthleteObj.birthDate ? formatDate(clubNonAthleteObj.birthDate) : '',
      orgRoleName: clubNonAthleteObj.orgRoleName,
      isSyncSelected: nonAthletesToSync.some((x) => x.memberId === clubNonAthleteObj.memberId) === true,
      roles
    };
  }

  function createSelectedNonAthletesState(formGridState, nonAthletesToSync = [], nonAthletesToTransfer = []) {
    const newSelectedNonAthletesState = {
      nonAthletesToSync: [...nonAthletesToSync],
      nonAthletesToTransfer: [...nonAthletesToTransfer]
    };

    for (const clubNonAthleteObj of formGridState) {
      if (clubNonAthleteObj.isSyncSelected === true) {
        const nonAthlete = {
          memberId: clubNonAthleteObj.memberId,
          name: `${clubNonAthleteObj.preferredName || clubNonAthleteObj.firstName} ${clubNonAthleteObj.lastName}`
        };

        if (nonAthletesToSync.some((x) => x.memberId === nonAthlete.memberId) === false) {
          newSelectedNonAthletesState.nonAthletesToSync.push(nonAthlete);
        }
      } else {
        const nonAthleteIndex = nonAthletesToSync.findIndex((x) => x.memberId === clubNonAthleteObj.memberId);

        if (nonAthleteIndex > -1) {
          newSelectedNonAthletesState.nonAthletesToSync.splice(nonAthleteIndex, 1);
        }
      }

      for (const role of clubNonAthleteObj.roles) {
        if (role.isTransferSelected === true) {
          const nonAthlete = {
            personOrgRoleDurationId: role.personOrgRoleDurationId,
            unClubId: role.unClubId,
            name: `${clubNonAthleteObj.preferredName || clubNonAthleteObj.firstName} ${clubNonAthleteObj.lastName} (${role.orgRoleName})`
          };

          if (nonAthletesToTransfer.some((x) => x.personOrgRoleDurationId === nonAthlete.personOrgRoleDurationId) === false) {
            newSelectedNonAthletesState.nonAthletesToTransfer.push(nonAthlete);
          }
        } else {
          const nonAthleteIndex = nonAthletesToTransfer.findIndex((x) => x.personOrgRoleDurationId === role.personOrgRoleDurationId);

          if (nonAthleteIndex > -1) {
            newSelectedNonAthletesState.nonAthletesToTransfer.splice(nonAthleteIndex, 1);
          }
        }
      }
    }

    return newSelectedNonAthletesState;
  }

  return {
    displayTransferPopup: displayTransferPopupState,
    formGridState,
    selectedNonAthletesState,
    onClearFilterClicked,
    onSyncSelectedClicked,
    onTransferSelectedClicked,
    onTransferPopupConfirmClicked,
    onTransferPopupCancelClicked,
    onUpdateIsSyncSelected,
    onUpdateIsTransferSelected
  };
};

export default useClubNonAthleteRosterFormGrid;